import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { websocketMessageReceived } from "./websocket";
import { API_BASE_URL } from "../../config";

export const fetchNotifications = createAsyncThunk(
  "notifications/fetchNotifications",
  async (_, { rejectWithValue }) => {
    try {
      const token = sessionStorage.getItem("token");
      if (!token) return rejectWithValue("No authentication token");

      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + token,
        },
      };

      const response = await fetch(
        `${API_BASE_URL}/api/avvisi/list`,
        requestOptions,
      );

      if (response.status === -1 || response.status === 0) {
        return rejectWithValue("Failed to fetch notifications");
      }

      const resData = await response.json();
      const data = resData.data.dataset;

      // Ensure data is an array
      if (Array.isArray(data)) {
        // Helper to parse "DD/MM/YYYY HH:mm:ss"
        const parseDate = (str) => {
          if (!str) return new Date(0);
          const [d, t] = str.split(" ");
          const [day, month, year] = d.split("/");
          const [h, m, s] = t ? t.split(":") : [0, 0, 0];
          return new Date(year, month - 1, day, h, m, s);
        };

        // Sort by date newest first
        const sortedData = [...data].sort(
          (a, b) => parseDate(b.dataCreazione) - parseDate(a.dataCreazione),
        );

        return {
          dataset: sortedData.map((item) => ({
            idUid: item.idUid,
            titolo: item.titolo,
            descrizione: item.descrizione || "",
            dataCreazione: item.dataCreazione,
            idTipologia: item.idTipologia,
            avvisiLetti: item.avvisiLetti || 0,
            avvisiNonLetti: item.avvisiNonLetti || 0,
            hasLike: item.hasLike || 0,
            hasComment: item.hasComment || 0,
            hasPopup: item.hasPopup || 0,
            isNew: false, // Fetched items are not "live" new
            avStatusRead: item.avStatusRead || 0,
            dataScadenza: item.dataScadenza || "",
          })),
          totUnRead: resData.data.totUnRead,
        };
      }
      return { dataset: [], totUnRead: 0 };
    } catch (error) {
      console.error("Error fetching notifications:", error);
      return rejectWithValue(error.message);
    }
  },
);

const initialState = {
  items: [],
  unreadCount: 0,
  loading: false,
  error: null,
  lastSentNotice: null, // Tracks the notice sent by the current session
  initialFetched: false, // Tracks if initial fetch after login has been completed
};

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    setNotifications: (state, action) => {
      state.items = action.payload;
      state.unreadCount = action.payload.filter(
        (n) => n.accettata !== "S",
      ).length;
    },
    markAsRead: (state, action) => {
      const notification = state.items.find((n) => n.idUid === action.payload);
      if (notification && notification.avStatusRead === 0) {
        notification.avStatusRead = 1;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    markAllAsRead: (state) => {
      state.items.forEach((n) => {
        n.avStatusRead = 1;
      });
      state.unreadCount = 0;
    },
    addNotification: (state, action) => {
      state.items.unshift(action.payload);
      state.unreadCount += 1;
    },
    setLastSentNotice: (state, action) => {
      state.lastSentNotice = {
        ...action.payload,
        timestamp: Date.now(),
      };
    },
    resetNotifications: (state) => {
      state.items = [];
      state.unreadCount = 0;
      state.loading = false;
      state.error = null;
      state.lastSentNotice = null;
      state.initialFetched = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(websocketMessageReceived, (state, action) => {
        const { type, data: message } = action.payload;
        if (message && (type === "notification" || type === "avvisi:new")) {
          // Map incoming socket data using standard camelCase keys
          const normalizedItem = {
            idUid: message.idUid,
            titolo: message.titolo,
            descrizione: message.description || message.descrizione || "",
            dataCreazione: message.dataCreazione || new Date().toLocaleString(),
            avStatusRead: 0,
            accettata: "N",
            idTipologia: message.idTipologia,
            avvisiLetti: 0,
            avvisiNonLetti: 1,
            hasLike: message.hasLike || 0,
            hasComment: message.hasComment || 0,
            hasPopup: message.hasPopup || 0,
            likes: message.likes || [],
            comments: message.comments || [],
            isNew: true, // Marked as new because it came from WebSocket
          };

          if (
            !state.items.find((item) => item.idUid === normalizedItem.idUid)
          ) {
            state.items.unshift(normalizedItem);
            state.unreadCount += 1;
          }
        }
      })
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.initialFetched = true;
        state.items = action.payload.dataset;
        state.unreadCount = action.payload.totUnRead;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setNotifications,
  markAsRead,
  markAllAsRead,
  addNotification,
  setLastSentNotice,
  resetNotifications,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
